/**
 * Storage ("Guardado") business rules — pure functions only (domain/AGENTS.md). Typed against a
 * minimal structural shape, not `contracts/storage.ts`'s full quote, the same way
 * `domain/shipment/shipment.rules.ts` takes a `ShipmentLike`.
 */

export interface QuoteServicesLike {
  storageProfileId: number | string;
  includedServices: string[];
}

export interface QuoteServiceFilterResult<T extends QuoteServicesLike> {
  /** Quotes that include every selected service. */
  included: T[];
  /** Quotes that miss at least one selected service, in their original order. */
  excluded: T[];
  /** Depósitos with no quote left in `included` — the number the client is told about. */
  excludedDepotCount: number;
}

/**
 * NEX-1166 — business doc §6, "filtros que no esconden opciones": the client filters quotes by the
 * services they include (every selected service at once), and is told how many depósitos the
 * filter leaves out so it can bring them back. A depósito with several quotes counts once, and is
 * not "left out" while any of its quotes still passes.
 */
export function filterQuotesByServices<T extends QuoteServicesLike>(quotes: T[], selectedCodes: string[]): QuoteServiceFilterResult<T> {
  if (!selectedCodes.length) return { included: [...quotes], excluded: [], excludedDepotCount: 0 };

  const included: T[] = [];
  const excluded: T[] = [];
  for (const quote of quotes) {
    const offers = new Set(quote.includedServices);
    (selectedCodes.every((code) => offers.has(code)) ? included : excluded).push(quote);
  }

  const includedDepots = new Set(included.map((q) => String(q.storageProfileId)));
  const excludedDepots = new Set(excluded.map((q) => String(q.storageProfileId)).filter((id) => !includedDepots.has(id)));
  return { included, excluded, excludedDepotCount: excludedDepots.size };
}

export interface QuoteValidityLike {
  validUntil: string | null;
}

/**
 * NEX-1167 — whether a quote is past its `validUntil`. The API stores a date-only vigencia as
 * 23:59:59.999 of that day in America/Montevideo and sends that instant in UTC (domain/quote-defaults.ts), so
 * comparing instants IS comparing Uruguayan calendar days, whatever the browser's zone. It is also
 * the comparison the checkout makes (create-storage-checkout.handler.ts: `new Date(validUntil) <
 * now`): comparing calendar days here instead would offer, for a quote saved before that change
 * (23:59:59.999Z), a payment the API refuses.
 */
export function isStorageQuotePastValidUntil(quote: QuoteValidityLike, now: Date = new Date()): boolean {
  return !!quote.validUntil && new Date(quote.validUntil).getTime() < now.getTime();
}

/**
 * NEX-1167 — a quote the client can still choose and pay: pending, and not past its own validUntil
 * (read directly: the job that flips a stale quote to `expired` is not assumed to have run yet).
 */
export function isStorageQuoteSelectable(quote: QuoteValidityLike & { status: string }, now: Date = new Date()): boolean {
  return quote.status === 'pending' && !isStorageQuotePastValidUntil(quote, now);
}

// ── NEX-1167 — choosing and paying a quote (business doc §6, "Selección y pago del guardado") ──

export interface AcceptedRequestLike {
  status: string;
  acceptedQuoteId?: number | string | null;
}

/**
 * NEX-1167 — the client's payment accepted a quote: the request is `accepted` AND records which quote
 * (nexvia-api-web writes both in the same UPDATE as the destination, `markAcceptedIfPublished`).
 */
export function hasStorageRequestAcceptedQuote(request: AcceptedRequestLike): boolean {
  return request.status === 'accepted' && request.acceptedQuoteId != null;
}

/** How the client came back from Mercado Pago: the `modality` of the return URL. */
export type StoragePaymentReturnModality = 'checkout' | 'subscription';

/** Which of the one-time checkout's three back URLs Mercado Pago used: a hint, never the state. */
export type StoragePaymentReturnHint = 'success' | 'failure' | 'pending' | null;

export type StoragePaymentReturnOutcome =
  /** No final answer yet: keep polling. */
  | 'checking'
  | 'paid'
  | 'pending_accreditation'
  /** The checkout order is still open and Mercado Pago sent the client back through `failure`. */
  | 'not_completed'
  | 'rejected'
  | 'expired'
  /** The order ended `cancelled`: the webhook refused an approved payment (request/quote no longer open). */
  | 'refused'
  /** NEX-1169 (plan 1.4) — Subscription: it was cancelled (by the client, Mercado Pago or Nexvia). */
  | 'subscription_cancelled'
  /** NEX-1169 (plan 1.4) — Subscription: Mercado Pago could not make a monthly charge (plan 1.6). */
  | 'subscription_past_due'
  /** NEX-1169 (plan 1.4) — Subscription: paused in Mercado Pago, no charges meanwhile. */
  | 'subscription_paused'
  /** No order for the request (checkout) / no subscription for the request (subscription). */
  | 'not_found'
  /** The polling time limit passed without a final answer. */
  | 'timed_out';

export interface StoragePaymentReturnInput {
  modality: StoragePaymentReturnModality;
  hint: StoragePaymentReturnHint;
  /** Checkout: `GET /storage/payment/status`'s order status; `undefined` = not answered yet, `null` = no order. */
  paymentStatus?: string | null;
  /**
   * NEX-1169 (plan 1.4) — Subscription: `GET /storage/payment/status`'s subscription status; `undefined` = not
   * answered yet, `null` = the request has no subscription.
   */
  subscriptionStatus?: string | null;
  timedOut: boolean;
}

// NEX-1169 (plan 1.4) — a subscription that no longer waits for Mercado Pago and was not authorized.
const SUBSCRIPTION_FINAL_OUTCOME: Record<string, StoragePaymentReturnOutcome> = {
  cancelled: 'subscription_cancelled',
  past_due: 'subscription_past_due',
  paused: 'subscription_paused'
};

/**
 * NEX-1169 (plan 1.4) — the monthly subscription waits for Mercado Pago only while `pending`: the client has
 * not authorized it yet, or its webhook has not arrived. Every other status does not change by itself.
 */
export function isStorageSubscriptionPending(status: string | null | undefined): boolean {
  return status === 'pending';
}

/**
 * NEX-1169 (plan 1.4) — Mercado Pago authorized the monthly charge: the webhook accepts the request with it,
 * so the client contracted the depósito.
 */
export function isStorageSubscriptionAuthorized(status: string | null | undefined): boolean {
  return status === 'authorized';
}

/** NEX-1169 (plan 1.4) — a cancelled subscription charges no more: the next payment date it kept is not one. */
export function isStorageSubscriptionCancelled(status: string | null | undefined): boolean {
  return status === 'cancelled';
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — a subscription that still charges or may charge: any known status but
 * `cancelled` (the API's "live" one, the only kind `POST .../subscription/cancel` accepts).
 */
export function isStorageSubscriptionLive(status: string | null | undefined): boolean {
  return !!status && !isStorageSubscriptionCancelled(status);
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.6) — Mercado Pago could not make a monthly charge: it retries during the grace
 * (`graceEndsAt`), and after it the API cancels the charge and gives the depósito the baja.
 */
export function isStorageSubscriptionPastDue(status: string | null | undefined): boolean {
  return status === 'past_due';
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — a pago único order that renews the guardado (`renewalMonths`), not the one
 * that contracted it (`null`, or absent on an API before 1.8).
 */
export function isStorageRenewalOrder(order: { renewalMonths?: number | null } | null | undefined): boolean {
  return order?.renewalMonths != null;
}

const CHECKOUT_FINAL_OUTCOME: Record<string, StoragePaymentReturnOutcome> = {
  paid: 'paid',
  pending_accreditation: 'pending_accreditation',
  rejected: 'rejected',
  expired: 'expired',
  cancelled: 'refused'
};

/**
 * NEX-1167 — what the payment return page (`/storage/payment/return`) tells the client. The pago
 * único reads the payment order (`GET /storage/payment/status/:id`). NEX-1169 (plan 1.4) — the
 * subscription reads that same endpoint's subscription state: `authorized` is paid, `pending` waits
 * (until the time limit), `cancelled` / `past_due` / `paused` are said as such. The `hint` (Mercado
 * Pago's back URL) only decides an order that is still open: `failure` means the client left without
 * paying; `success`/`pending` mean the webhook has not arrived yet.
 */
export function getStoragePaymentReturnOutcome(input: StoragePaymentReturnInput): StoragePaymentReturnOutcome {
  const waiting: StoragePaymentReturnOutcome = input.timedOut ? 'timed_out' : 'checking';

  if (input.modality === 'subscription') {
    const { subscriptionStatus } = input;
    if (subscriptionStatus === undefined) return waiting;
    if (subscriptionStatus === null) return 'not_found';
    if (isStorageSubscriptionAuthorized(subscriptionStatus)) return 'paid';
    // [ASSUMED] a status this app does not know yet keeps checking until the time limit, like `pending`.
    return SUBSCRIPTION_FINAL_OUTCOME[subscriptionStatus] ?? waiting;
  }

  const { paymentStatus } = input;
  if (paymentStatus === undefined) return waiting;
  if (paymentStatus === null) return 'not_found';
  if (CHECKOUT_FINAL_OUTCOME[paymentStatus]) return CHECKOUT_FINAL_OUTCOME[paymentStatus];
  if (paymentStatus === 'pending_payment' && input.hint === 'failure') return 'not_completed';
  return waiting;
}

const UNSETTLED_RETURN_OUTCOMES: ReadonlyArray<StoragePaymentReturnOutcome> = ['checking', 'pending_accreditation', 'not_completed'];

/** NEX-1167 — the outcome no longer changes by itself, so the return page stops polling. */
export function isStoragePaymentReturnSettled(outcome: StoragePaymentReturnOutcome): boolean {
  return !UNSETTLED_RETURN_OUTCOMES.includes(outcome);
}

/**
 * NEX-1167 — business doc §6, "Cotización del traslado", once the guardado is paid:
 * - `included`: the accepted quote includes the depósito's own transport — resolved there.
 * - `quote_with_nexvia`: the client asked to quote it with Nexvia and the quote does not include
 *   it — quoted "como cualquier envío" (the shipment wizard, destination = the depósito).
 * - `self`: the client said they would handle it.
 * - `none`: the client did not ask for transport.
 * `null` while the request is not accepted (there is no depósito, so no destination yet).
 * NEX-1170 — `null` too once the depósito confirmed the reception (`storageStatus` past `contracted`):
 * the transport to the depósito is over, so there is nothing left to quote or coordinate.
 */
export type StorageTransportStep = 'included' | 'quote_with_nexvia' | 'self' | 'none';

export function getStorageTransportStep(
  request: { status: string; needsTransport: boolean; transportQuotedByNexvia?: boolean; storageStatus?: string | null },
  acceptedQuote: { transportIncluded: boolean } | null | undefined
): StorageTransportStep | null {
  if (request.status !== 'accepted') return null;
  if (request.storageStatus && request.storageStatus !== 'contracted') return null;
  if (acceptedQuote?.transportIncluded) return 'included';
  if (!request.needsTransport) return 'none';
  return request.transportQuotedByNexvia ? 'quote_with_nexvia' : 'self';
}

/**
 * NEX-1169 (plan Fase 7.2) — the pago único pays the whole period, so it needs one: nexvia-api-web's
 * checkout answers 422 PAYMENT_STORAGE_PERIOD_OPEN_ENDED for an open-ended request and
 * PAYMENT_STORAGE_PERIOD_INVALID for one without a length. Those requests are paid monthly only.
 */
export function canPayStorageFullPeriod(request: { openEnded: boolean; periodMonths: number | null }): boolean {
  return !request.openEnded && !!request.periodMonths && request.periodMonths > 0;
}

// ── NEX-1170 — the guardado after contracting it (plan Fase 8, nexvia-api-web domain/storage-lifecycle.ts) ──

/** The guardado's lifecycle fields a rule reads (`GET /storage/requests/...`'s request). */
export interface StorageGuardadoLike {
  storageStatus?: string | null;
  paymentModality?: string | null;
}

/** A payment accepted a quote: the request has a lifecycle (contracted → active → ending → ended). */
export function isStorageGuardadoContracted(guardado: StorageGuardadoLike): boolean {
  return !!guardado.storageStatus;
}

/** "Dar de baja": before or after the reception, while no baja runs (ALLOWED_FROM.cancel). */
export function canCancelStorageGuardado(guardado: StorageGuardadoLike): boolean {
  return guardado.storageStatus === 'contracted' || guardado.storageStatus === 'active';
}

/**
 * "Solicitar retiro" and "Retiré por mi cuenta": the things are in the depósito (reception confirmed),
 * also while a baja runs, which is when it is expected (ALLOWED_FROM.request_withdrawal/self_withdrawal).
 */
export function canWithdrawStorageGuardado(guardado: StorageGuardadoLike): boolean {
  return guardado.storageStatus === 'active' || guardado.storageStatus === 'ending';
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — «Renovar»: only an `active` guardado (ALLOWED_FROM.renew: a `contracted`
 * one has no end yet, an `ending` one is being cancelled; else 409 STORAGE_RENEWAL_NOT_ALLOWED), never a monthly
 * one — its subscription is the renewal (409 STORAGE_RENEWAL_IS_THE_SUBSCRIPTION). An unknown modality (`null`)
 * counts, as it does for the API.
 */
export function canRenewStorageGuardado(guardado: StorageGuardadoLike): boolean {
  return guardado.storageStatus === 'active' && guardado.paymentModality !== 'subscription';
}

/** NEX-1169 (plan 2026-10-20 task 1.8) — how many months a renewal may add (the API's body: 1–36). */
export const STORAGE_RENEWAL_MONTHS = { min: 1, max: 36 } as const;

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — «Cancelar el cobro mensual» (ALLOWED_FROM.cancel_subscription): a
 * contracted, active or ending guardado whose subscription is still live (`GET /storage/payment/status`). An
 * `ending` one with a live subscription is a baja whose cancellation Mercado Pago did not confirm: the call
 * retries it. Never without the subscription's status (no dead button).
 */
export function canCancelStorageSubscription(guardado: StorageGuardadoLike, subscriptionStatus: string | null | undefined): boolean {
  const running = guardado.storageStatus === 'contracted' || guardado.storageStatus === 'active' || guardado.storageStatus === 'ending';
  return running && isStorageSubscriptionLive(subscriptionStatus);
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — the baja ran (`ending`) but the monthly charge is still live: Mercado Pago
 * did not confirm its cancellation (the API's `manual_pending`), so Nexvia cancels it by hand. Since 1.5 every baja
 * cancels it in Mercado Pago, so a cancelled one says nothing.
 */
export function isStorageMonthlyChargeLeftToNexvia(guardado: StorageGuardadoLike, subscriptionStatus: string | null | undefined): boolean {
  return guardado.storageStatus === 'ending' && isStorageSubscriptionLive(subscriptionStatus);
}

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — a running guardado paid by a subscription that renews itself
 * (`autoRenew`): its `endsAt` is the last day paid so far, which each approved charge moves a month on.
 */
export function isStorageGuardadoRenewingMonthly(
  guardado: StorageGuardadoLike,
  subscription: { status?: string | null; autoRenew?: boolean | null } | null | undefined
): boolean {
  return canCancelStorageGuardado(guardado) && subscription?.autoRenew === true && isStorageSubscriptionLive(subscription.status);
}

// ── NEX-1329 — the guardado's mutual reviews (plan Fase 10, decision A6) ──

/** The client's side of the reviews on a row of `GET /storage/requests/mine`. */
export interface StorageReviewStateLike {
  canReview?: boolean | null;
  myReview?: unknown;
  theirReview?: unknown;
}

/**
 * The "Calificá al depósito" card: from the reception until the client rates the depósito (`canReview`,
 * decided by the API — nexvia-api-web domain/guardado-review.ts), and afterwards while there is a review
 * to show (the client's own or the one the depósito left about the client).
 */
export function shouldShowStorageReviewCard(state: StorageReviewStateLike): boolean {
  return state.canReview === true || !!state.myReview || !!state.theirReview;
}

/** The depósito has reviews to show next to its quote (`GET /storage/requests/:id/quotes`). */
export function hasStorageDepotReviews(deposit: { averageRating?: number | null; reviewCount?: number | null }): boolean {
  return deposit.averageRating != null && (deposit.reviewCount ?? 0) > 0;
}
